import { useContext, useEffect, useMemo } from 'react';
import _ from 'lodash';

import ReportGadgetsContext from '../../contexts/ReportGadgetsContext';
import isV5ChartDef from '../../types/visTypeCheckers/isV5ChartDef';
import isSingleMetricDateMatrix from '../../types/visTypeCheckers/isSingleMetricDateMatrix';
import isDefined from '../../isDefined';
import cardTypeCheckers from '../../types/cardTypeCheckers';
import MetricOptionsContext from '../../contexts/MetricOptionsContext';
import isRankingMatrix from '../../types/visTypeCheckers/isRankingMatrix';
import isGauge from '../../types/visTypeCheckers/isGauge';
import captureException from '../../services/captureException';
import metricTypeCheckers from '../../types/metricTypeCheckers';

const useMetricsOnReport = (report: ReportType | undefined) => {
  const { metricOptions, normalMetrics } = useContext(MetricOptionsContext);
  const { allGadgets } = useContext(ReportGadgetsContext);

  const usedDefinitions = useMemo(() => {
    if (!report) {
      return [];
    }

    return report.canvas.cards
      .map((c) => {
        if (cardTypeCheckers.isReportGadget(c)) {
          const gadget = allGadgets.find(
            (g) => g.id === c.content.reportGadgetId,
          );
          return gadget ? gadget.chartDef : undefined;
        }

        return undefined;
      })
      .filter(isDefined);
  }, [allGadgets, report]);

  useEffect(() => {
    if (!report) {
      return;
    }

    const unknownDefinitionType = usedDefinitions.find((d) => {
      const isV5 = isV5ChartDef(d);
      const isRankingMatrixG = isRankingMatrix(d);
      const isSingleMetricDate = isSingleMetricDateMatrix(d);
      const isGaugeG = isGauge(d);
      const isKnownType =
        isV5 || isRankingMatrixG || isSingleMetricDate || isGaugeG;
      if (isKnownType) {
        return false;
      }
      return true;
    });
    if (unknownDefinitionType) {
      const e = new Error();
      e.name = `Unknown Definition Type in Report: ${unknownDefinitionType.name} - ${report.id}`;
      captureException(e);
    }
  }, [report, usedDefinitions]);

  const { metrics, compoundMetrics, specialMetrics } = useMemo(() => {
    const metricIdFromSingleMetricDateMatrix = usedDefinitions
      .filter(isSingleMetricDateMatrix)
      .map((d) => d.metricId);
    const metricIdsFromRankingMatrix = usedDefinitions
      .filter(isRankingMatrix)
      .map((d) => d.metrics.map((m) => m.metricId))
      .reduce((acc, mIds) => {
        return [...acc, ...mIds];
      }, [] as string[]);
    const metricIdsFromGauges = usedDefinitions
      .filter(isGauge)
      .map((g) => g.metricId);

    const metricIdsFromOtherChartTypes = [
      ...metricIdFromSingleMetricDateMatrix,
      ...metricIdsFromRankingMatrix,
      ...metricIdsFromGauges,
    ];

    const fromMatrix = (() => {
      const m = [] as Metrics.NormalMetric[];
      const cm = [] as Metrics.CompoundMetric[];
      const sm = [] as Metrics.SpecialMetric[];

      metricIdsFromOtherChartTypes.forEach((mId) => {
        const metric = metricOptions.find((mo) => mo.id === mId);
        if (metric) {
          if (metricTypeCheckers.isNormalMetric(metric)) {
            m.push(metric);
          } else if (metricTypeCheckers.isCompoundMetric(metric)) {
            cm.push(metric);

            const inputMetrics = metric.metricIds
              .map((inputMId) => {
                return normalMetrics.find((nm) => nm.id === inputMId);
              })
              .filter(metricTypeCheckers.isNormalMetric)
              .filter(isDefined);
            m.push(...inputMetrics);
          } else if (metricTypeCheckers.isSpecialMetric(metric)) {
            sm.push(metric);
          }
        }
      });

      return {
        m,
        cm,
        sm,
      };
    })();

    const fromSeries = (() => {
      const m = [] as Metrics.NormalMetric[];
      const cm = [] as Metrics.CompoundMetric[];
      const sm = [] as Metrics.SpecialMetric[];

      usedDefinitions.filter(isV5ChartDef).forEach((def) => {
        const { series } = def;
        series.forEach((s) => {
          const metric = metricOptions.find((mo) => mo.id === s.metricId);
          if (metric) {
            if (metricTypeCheckers.isNormalMetric(metric)) {
              m.push(metric);
            } else if (metricTypeCheckers.isCompoundMetric(metric)) {
              cm.push(metric);

              const inputMetrics = metric.metricIds
                .map((inputMId) => {
                  return normalMetrics.find((nm) => nm.id === inputMId);
                })
                .filter(isDefined)
                .filter(metricTypeCheckers.isNormalMetric);
              m.push(...inputMetrics);
            } else if (metricTypeCheckers.isSpecialMetric(metric)) {
              sm.push(metric);
            }
          }
        });
      });

      return {
        m,
        cm,
        sm,
      };
    })();

    return {
      metrics: _.uniq([...fromMatrix.m, ...fromSeries.m]),
      compoundMetrics: _.uniq([...fromMatrix.cm, ...fromSeries.cm]),
      specialMetrics: _.uniq([...fromMatrix.sm, ...fromSeries.sm]),
    };
  }, [metricOptions, normalMetrics, usedDefinitions]);

  return {
    metrics,
    compoundMetrics,
    specialMetrics,
  };
};

export default useMetricsOnReport;
